import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "../components/Layout/AdminLayout";

interface Review {
    id: number;
    customer_name: string;
    customer_image?: string | null;
    customer_images?: string[] | string | null;
    rating: number;
    review_text: string;
    product_name?: string | null;
    is_verified: boolean;
    status: "PUBLISHED" | "HIDDEN";
    created_at: string;
}

interface FormData {
    customerName: string;
    customerImage: string;
    customerImages: string[];
    rating: number;
    reviewText: string;
    productName: string;
    isVerified: boolean;
    status: "PUBLISHED" | "HIDDEN";
}

// const API_URL = "http://localhost:8483";
const API_URL = new URL(
  "https://www.ayushvatika.com"
).origin;


const emptyForm: FormData = {
    customerName: "",
    customerImage: "",
    customerImages: [],
    rating: 5,
    reviewText: "",
    productName: "",
    isVerified: false,
    status: "PUBLISHED"
};


const AdminReviews: React.FC = () => {

    const [reviews, setReviews] = useState<Review[]>([]);

    const [showModal, setShowModal] =
        useState(false);

    const [editingId, setEditingId] =
        useState<number | null>(null);

    const [form, setForm] =
        useState<FormData>(emptyForm);

    const [loading, setLoading] =
        useState(false);

    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const imageInputRef = useRef<HTMLInputElement | null>(null);


    // ==========================================
    // FETCH
    // ==========================================

    const fetchReviews = async () => {

        try {

            const response = await fetch(
                `${API_URL}/api/reviews/admin/all`
            );

            const data = await response.json();

            if (data.success) {
                setReviews(data.reviews);
            }

        } catch (error) {

            console.error(error);

        }
    };


    useEffect(() => {

        fetchReviews();

    }, []);


    // ==========================================
    // FORM
    // ==========================================

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement |
            HTMLTextAreaElement |
            HTMLSelectElement
        >
    ) => {

        const { name, value, type } = e.target;

        setForm(prev => ({
            ...prev,
            [name]:
                type === "checkbox"
                    ? (e.target as HTMLInputElement).checked
                    : value
        }));

    };


    const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 3) {
            alert("Maximum 3 images select kar sakte ho.");
            e.target.value = "";
            return;
        }
        const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/bmp", "image/tiff", "image/avif", "image/svg+xml"];
        const invalid = files.find(file => !allowed.includes(file.type));
        if (invalid) {
            alert("Please select image files only (JPG, PNG, WebP, GIF, BMP, TIFF, AVIF or SVG).");
            e.target.value = "";
            return;
        }
        const tooLarge = files.find(file => file.size > 10 * 1024 * 1024);
        if (tooLarge) {
            alert("Har image 10MB se chhoti honi chahiye.");
            e.target.value = "";
            return;
        }
        setSelectedImages(files);
        setImagePreviews(files.map(file => URL.createObjectURL(file)));
    };

    const removeImagePreview = (index: number) => {
        const isNewSelection = selectedImages.length > 0;
        if (isNewSelection) {
            const next = selectedImages.filter((_, i) => i !== index);
            setSelectedImages(next);
            setImagePreviews(next.map(file => URL.createObjectURL(file)));
            if (imageInputRef.current) imageInputRef.current.value = "";
        } else {
            const next = imagePreviews.filter((_, i) => i !== index);
            setImagePreviews(next);
            setForm(prev => ({ ...prev, customerImages: next, customerImage: next[0] || "" }));
        }
    };


    // ==========================================
    // SAVE
    // ==========================================

    const handleSubmit = async (
        e: React.FormEvent
    ) => {

        e.preventDefault();

        setLoading(true);

        try {

            const url = editingId
                ? `${API_URL}/api/reviews/admin/${editingId}`
                : `${API_URL}/api/reviews/admin`;

            const method =
                editingId ? "PUT" : "POST";


            const formData = new FormData();
            Object.entries(form).forEach(([key, value]) => {
                if (key === "customerImages") return;
                formData.append(key, String(value));
            });
            selectedImages.forEach((file) => {
                formData.append("customerImages", file);
            });

            const response = await fetch(url, {
                method,
                body: formData
            });


            const data = await response.json();


            if (!response.ok) {

                alert(
                    data.message ||
                    "Something went wrong"
                );

                return;
            }


            alert(
                editingId
                    ? "Review updated successfully"
                    : "Review added successfully"
            );


            setShowModal(false);
            setEditingId(null);
            setForm(emptyForm);
            setSelectedImages([]);
            setImagePreviews([]);
            if (imageInputRef.current) imageInputRef.current.value = "";

            fetchReviews();

        } catch (error) {

            console.error(error);

            alert("Server error");

        } finally {

            setLoading(false);

        }

    };


    // ==========================================
    // EDIT
    // ==========================================

    const handleEdit = (review: Review) => {

        setEditingId(review.id);

        let savedImages: string[] = [];
        try {
            const rawImages = review.customer_images;
            if (Array.isArray(rawImages)) savedImages = rawImages;
            else if (typeof rawImages === "string" && rawImages.trim()) {
                const parsed = JSON.parse(rawImages);
                savedImages = Array.isArray(parsed) ? parsed : [rawImages];
            } else if (review.customer_image) {
                savedImages = [review.customer_image];
            }
        } catch {
            if (review.customer_image) savedImages = [review.customer_image];
        }

        setSelectedImages([]);
        setImagePreviews(savedImages);
        setForm({
            customerName: review.customer_name,
            customerImage: review.customer_image || "",
            customerImages: savedImages,
            rating: review.rating,
            reviewText: review.review_text,
            productName: review.product_name || "",
            isVerified: review.is_verified,
            status: review.status
        });

        if (imageInputRef.current) imageInputRef.current.value = "";
        setShowModal(true);
    };


    // ==========================================
    // DELETE
    // ==========================================

    const handleDelete = async (id: number) => {

        const confirmDelete =
            window.confirm(
                "Are you sure you want to delete this review?"
            );

        if (!confirmDelete) return;


        try {

            const response = await fetch(
                `${API_URL}/api/reviews/admin/${id}`,
                {
                    method: "DELETE"
                }
            );


            const data =
                await response.json();


            if (data.success) {

                fetchReviews();

            } else {

                alert(data.message);

            }

        } catch (error) {

            console.error(error);

        }

    };


    // ==========================================
    // STATUS
    // ==========================================

    const toggleStatus = async (
        review: Review
    ) => {

        const newStatus =
            review.status === "PUBLISHED"
                ? "HIDDEN"
                : "PUBLISHED";


        try {

            const response = await fetch(
                `${API_URL}/api/reviews/admin/${review.id}/status`,
                {
                    method: "PATCH",

                    headers: {
                        "Content-Type":
                            "application/json"
                    },

                    body: JSON.stringify({
                        status: newStatus
                    })
                }
            );


            const data =
                await response.json();


            if (data.success) {
                fetchReviews();
            }

        } catch (error) {

            console.error(error);

        }

    };


    return (
    <AdminLayout>
        <div className="p-6 bg-gray-50 min-h-screen">
         

            {/* Header */}
           

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                 

                <div>

                    <h1 className="text-3xl font-bold text-gray-900">
                        Customer Reviews
                    </h1>

                    <p className="text-gray-500 mt-1">
                        Manage customer reviews displayed on Ayushvatika.
                    </p>

                </div>


                <button
                    onClick={() => {

                        setEditingId(null);
                        setForm(emptyForm);
                        setSelectedImages([]);
                        setImagePreviews([]);
                        if (imageInputRef.current) imageInputRef.current.value = "";
                        setShowModal(true);

                    }}
                    className="bg-green-700 hover:bg-green-800 text-white px-5 py-3 rounded-xl font-semibold transition"
                >
                    + Add Review
                </button>

            </div>


            {/* Stats */}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">

                <div className="bg-white rounded-2xl p-5 border border-gray-100">

                    <p className="text-gray-500 text-sm">
                        Total Reviews
                    </p>

                    <p className="text-3xl font-bold mt-1">
                        {reviews.length}
                    </p>

                </div>


                <div className="bg-white rounded-2xl p-5 border border-gray-100">

                    <p className="text-gray-500 text-sm">
                        Published
                    </p>

                    <p className="text-3xl font-bold text-green-700 mt-1">
                        {
                            reviews.filter(
                                r => r.status === "PUBLISHED"
                            ).length
                        }
                    </p>

                </div>


                <div className="bg-white rounded-2xl p-5 border border-gray-100">

                    <p className="text-gray-500 text-sm">
                        Hidden
                    </p>

                    <p className="text-3xl font-bold text-gray-500 mt-1">
                        {
                            reviews.filter(
                                r => r.status === "HIDDEN"
                            ).length
                        }
                    </p>

                </div>

            </div>


            {/* Reviews Table */}

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">

                <div className="overflow-x-auto">

                    <table className="w-full">

                        <thead className="bg-gray-50">

                            <tr>

                                <th className="text-left px-5 py-4">
                                    Customer
                                </th>

                                <th className="text-left px-5 py-4">
                                    Rating
                                </th>

                                <th className="text-left px-5 py-4">
                                    Review
                                </th>

                                <th className="text-left px-5 py-4">
                                    Product
                                </th>

                                <th className="text-left px-5 py-4">
                                    Status
                                </th>

                                <th className="text-right px-5 py-4">
                                    Actions
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {reviews.map(review => (

                                <tr
                                    key={review.id}
                                    className="border-t border-gray-100"
                                >

                                    <td className="px-5 py-4">

                                        <div className="font-semibold">
                                            {review.customer_name}
                                        </div>

                                        {review.is_verified && (

                                            <span className="text-xs text-green-700">
                                                ✓ Verified
                                            </span>

                                        )}

                                    </td>


                                    <td className="px-5 py-4">

                                        <div className="text-[#fbbc04]">
                                            {"★".repeat(review.rating)}
                                        </div>

                                    </td>


                                    <td className="px-5 py-4 max-w-sm">

                                        <p className="text-sm text-gray-600 line-clamp-2">
                                            {review.review_text}
                                        </p>

                                    </td>


                                    <td className="px-5 py-4 text-sm">
                                        {review.product_name || "-"}
                                    </td>


                                    <td className="px-5 py-4">

                                        <button
                                            onClick={() =>
                                                toggleStatus(review)
                                            }
                                            className={
                                                review.status === "PUBLISHED"
                                                    ? "px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"
                                                    : "px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600"
                                            }
                                        >
                                            {review.status}
                                        </button>

                                    </td>


                                    <td className="px-5 py-4">

                                        <div className="flex justify-end gap-2">

                                            <button
                                                onClick={() =>
                                                    handleEdit(review)
                                                }
                                                className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium"
                                            >
                                                Edit
                                            </button>


                                            <button
                                                onClick={() =>
                                                    handleDelete(review.id)
                                                }
                                                className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm font-medium"
                                            >
                                                Delete
                                            </button>

                                        </div>

                                    </td>

                                </tr>

                            ))}

                        </tbody>

                    </table>

                </div>

            </div>


            {/* MODAL */}

            {showModal && (

                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">

                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

                        <div className="p-6 border-b flex items-center justify-between">

                            <h2 className="text-xl font-bold">
                                {editingId
                                    ? "Edit Review"
                                    : "Add Customer Review"}
                            </h2>

                            <button
                                onClick={() =>
                                    setShowModal(false)
                                }
                                className="text-gray-500 text-2xl"
                            >
                                ×
                            </button>

                        </div>


                        <form
                            onSubmit={handleSubmit}
                            className="p-6 space-y-5"
                        >

                            {/* Name */}

                            <div>

                                <label className="block font-medium mb-2">
                                    Customer Name
                                </label>

                                <input
                                    name="customerName"
                                    value={form.customerName}
                                    onChange={handleChange}
                                    required
                                    className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-green-500"
                                    placeholder="Rohan Sharma"
                                />

                            </div>


                            {/* Customer images */}
                            <div>
                                <label className="block font-medium mb-2">
                                    Customer Review Images (maximum 3)
                                </label>
                                <input
                                    ref={imageInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleImagesChange}
                                    className="w-full border rounded-xl px-4 py-3"
                                />
                                <p className="mt-2 text-xs text-gray-500">
                                    Apne system se 1–3 images select karo. Supported image formats automatically WebP mein convert hokar save honge.
                                    {editingId ? " Nayi images select karoge to existing images replace hongi; select nahi karoge to existing images rahengi." : ""}
                                </p>
                                {imagePreviews.length > 0 && (
                                    <div className="mt-3 grid grid-cols-3 gap-3">
                                        {imagePreviews.map((src, index) => (
                                            <div key={`${src}-${index}`} className="relative overflow-hidden rounded-xl border">
                                                <img src={src} alt={`Review preview ${index + 1}`} className="h-28 w-full object-cover" />
                                                <button
                                                    type="button"
                                                    onClick={() => removeImagePreview(index)}
                                                    className="absolute right-1 top-1 rounded-full bg-black/70 px-2 py-1 text-xs text-white"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>


                            {/* Rating */}

                            <div>

                                <label className="block font-medium mb-2">
                                    Rating
                                </label>

                                <select
                                    name="rating"
                                    value={form.rating}
                                    onChange={handleChange}
                                    className="w-full border rounded-xl px-4 py-3"
                                >

                                    <option value={5}>
                                        ★★★★★ — 5
                                    </option>

                                    <option value={4}>
                                        ★★★★☆ — 4
                                    </option>

                                    <option value={3}>
                                        ★★★☆☆ — 3
                                    </option>

                                    <option value={2}>
                                        ★★☆☆☆ — 2
                                    </option>

                                    <option value={1}>
                                        ★☆☆☆☆ — 1
                                    </option>

                                </select>

                            </div>


                            {/* Review */}

                            <div>

                                <label className="block font-medium mb-2">
                                    Review
                                </label>

                                <textarea
                                    name="reviewText"
                                    value={form.reviewText}
                                    onChange={handleChange}
                                    required
                                    rows={5}
                                    className="w-full border rounded-xl px-4 py-3"
                                    placeholder="Customer review..."
                                />

                            </div>


                            {/* Product */}

                            <div>

                                <label className="block font-medium mb-2">
                                    Product Name
                                </label>

                                <input
                                    name="productName"
                                    value={form.productName}
                                    onChange={handleChange}
                                    className="w-full border rounded-xl px-4 py-3"
                                    placeholder="Asthi Shuddhi Tel"
                                />

                            </div>


                            {/* Verified */}

                            <label className="flex items-center gap-3">

                                <input
                                    type="checkbox"
                                    name="isVerified"
                                    checked={form.isVerified}
                                    onChange={handleChange}
                                    className="w-5 h-5"
                                />

                                <span className="font-medium">
                                    Verified Purchase
                                </span>

                            </label>


                            {/* Status */}

                            <div>

                                <label className="block font-medium mb-2">
                                    Status
                                </label>

                                <select
                                    name="status"
                                    value={form.status}
                                    onChange={handleChange}
                                    className="w-full border rounded-xl px-4 py-3"
                                >

                                    <option value="PUBLISHED">
                                        Published
                                    </option>

                                    <option value="HIDDEN">
                                        Hidden
                                    </option>

                                </select>

                            </div>


                            {/* Buttons */}

                            <div className="flex justify-end gap-3 pt-4">

                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowModal(false)
                                    }
                                    className="px-5 py-3 rounded-xl border"
                                >
                                    Cancel
                                </button>


                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-5 py-3 rounded-xl bg-green-700 text-white font-semibold disabled:opacity-50"
                                >
                                    {loading
                                        ? "Saving..."
                                        : editingId
                                            ? "Update Review"
                                            : "Add Review"}
                                </button>

                            </div>

                        </form>

                    </div>

                </div>

            )}

        </div>

    </AdminLayout>

    );
};

export default AdminReviews;